import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontRoboto, theme } from "shared/lib/styles";

import { AuthorAvatar } from "scmp-app/components/author/author-avatar";
import { AuthorSocialLinks } from "scmp-app/components/author/author-social-links";
import { Button } from "scmp-app/components/common/button";
import { EntityFollowButton } from "scmp-app/components/entity-follow-button";
import { SCMPLabLogo } from "scmp-app/components/scmp-lab-logo";

export const Avatar = styled(AuthorAvatar)`
  inline-size: 80px;
  block-size: 80px;
  box-sizing: content-box;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 150px;
    block-size: 150px;
    margin-inline-end: 28px;
  }
`;

export const Name = styled.h1`
  margin-block-start: 20px;

  color: #333333;
  font-weight: 900;
  font-size: 20px;
  font-family: ${fontRoboto};
  line-height: 23px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 0;

    font-size: 28px;
    line-height: 33px;
  }
`;

export const Follow = styled(EntityFollowButton)`
  margin-block-start: 12px;
`;

export const Location = styled.div`
  color: #333333;
  font-size: 16px;
  font-family: ${fontRoboto};
  font-style: italic;
  line-height: 19px;

  ${theme.breakpoints.up("tablet")} {
    font-size: 18px;
    line-height: 21px;

    :not(:only-child) {
      padding-inline-end: 12px;
      border-inline-end: 1px solid #333333;
    }
  }
`;

export const TwitterLink = styled.a`
  color: #4585ff;
  line-height: 19px;

  ${theme.breakpoints.up("tablet")} {
    font-size: 18px;
    line-height: 21px;
  }
`;

export const SocialLinks = styled(AuthorSocialLinks)``;

export const LocationAndSocialSubGrid = styled.div`
  &:not(:empty) {
    margin-block-start: 20px;
  }

  ${theme.breakpoints.only("mobile")} {
    ${Location} {
      :not(:only-child) {
        margin-block-end: 8px;
      }
    }

    > :not(${Location}) + :not(${Location}) {
      margin-inline-start: 20px;
    }
  }

  ${theme.breakpoints.up("tablet")} {
    &:not(:empty) {
      margin-block-start: 16px;
    }

    > * {
      place-self: center start;
    }

    > * + * {
      margin-inline-start: 12px;
    }
  }
`;

export const BasicInformationSubGrid = styled.div`
  &:not(:empty) {
    margin-block-start: 20px;

    ${theme.breakpoints.up("tablet")} {
      margin-block-start: 40px;
    }
  }
`;

export const Role = styled.div`
  margin-block-end: 12px;

  color: #666666;
  font-weight: 900;
  font-size: 16px;
  font-family: ${fontRoboto};
  text-transform: capitalize;

  ${theme.breakpoints.up("tablet")} {
    font-size: 18px;
    line-height: 21px;
  }
`;

const Bio = styled.div`
  color: #666666;
  line-height: 21px;
`;

export const TabletUpBio = styled(Bio)`
  ${theme.breakpoints.only("mobile")} {
    display: none;
  }
`;

export const MobileBio = styled(Bio)`
  ${theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const ExtraInformationItemLabel = styled.div`
  margin-block-end: 8px;

  font-weight: 700;
  line-height: 19px;
`;

const extraInformationStyle = css`
  margin-block-start: 20px;

  color: #666666;
  line-height: 21px;
`;

export const Education = styled.div`
  ${extraInformationStyle}
`;

export const Expertise = styled.div`
  ${extraInformationStyle}
`;

export const Language = styled.div`
  ${extraInformationStyle}
`;

export const UpTriangle = styled.span`
  inline-size: 0;
  block-size: 0;

  border-color: transparent transparent #4e8cff;
  border-style: solid;
  border-width: 0 8px 8px;
`;

export const DownTriangle = styled.span`
  inline-size: 0;
  block-size: 0;

  border-color: #4e8cff transparent transparent;
  border-style: solid;
  border-width: 8px 8px 0;
`;

export const MobileMoreInformation = styled.div`
  ${theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const TabletUpMoreInformation = styled.div`
  ${theme.breakpoints.only("mobile")} {
    display: none;
  }
`;

export const MoreButton = styled(Button)`
  flex-direction: row;

  margin-block-start: 20px;
  padding: 0;

  color: #4e8bff;
  text-transform: none;

  &&:hover {
    background: transparent !important;
  }

  ${UpTriangle} {
    margin-inline-start: 10px;
  }
  ${DownTriangle} {
    margin-inline-start: 10px;
  }

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }
`;

export const StyledSCMPLabLogo = styled(SCMPLabLogo)`
  display: flex;
  justify-content: start;

  margin-block-start: 16px;
`;

export const Container = styled.div`
  display: grid;
  grid:
    "avatar avatar" min-content
    "name name" min-content
    "follow follow" min-content
    "location location" min-content
    "social social" min-content
    "role role" min-content
    "bio bio" min-content
    "more-info more-info" min-content
    "more more" min-content
    / max-content 1fr;

  font-size: 16px;
  font-family: ${fontRoboto};

  :has(${StyledSCMPLabLogo}) {
    grid:
      "avatar avatar" min-content
      "name name" min-content
      "follow follow" min-content
      "scmp-lab-logo scmp-lab-logo" min-content
      "location location" min-content
      "social social" min-content
      "role role" min-content
      "bio bio" min-content
      "more-info more-info" min-content
      "more more" min-content
      / max-content 1fr;
  }

  ${theme.breakpoints.up("tablet")} {
    grid:
      "avatar . . ." min-content
      "avatar name name name" min-content
      "avatar follow follow follow" min-content
      "avatar location twitter social" min-content
      "avatar . . ." min-content
      "role role role role" min-content
      "bio bio bio bio" min-content
      "more-info more-info more-info more-info" min-content
      "more more more more" min-content
      / max-content max-content max-content 1fr;

    :has(${StyledSCMPLabLogo}) {
      grid:
        "avatar . . ." min-content
        "avatar name name name" min-content
        "avatar follow follow follow" min-content
        "avatar scmp-lab-logo scmp-lab-logo scmp-lab-logo" min-content
        "avatar location twitter social" min-content
        "avatar . . ." min-content
        "role role role role" min-content
        "bio bio bio bio" min-content
        "more-info more-info more-info more-info" min-content
        "more more more more" min-content
        / max-content max-content max-content 1fr;
    }
  }

  ${Avatar} {
    grid-area: avatar;
  }

  ${Name} {
    grid-area: name;
  }

  ${Follow} {
    grid-area: follow;
  }

  ${LocationAndSocialSubGrid} {
    grid-row: location-start / social-end;
    grid-column: location-start / social-end;

    display: grid;
    grid:
      "location" min-content "social" min-content /
      max-content;

    ${theme.breakpoints.up("tablet")} {
      grid:
        "location twitter social" min-content
        / max-content max-content max-content;
    }
  }

  ${Location} {
    grid-area: location;
  }

  ${TwitterLink}, ${SocialLinks} {
    grid-row: social-start / span 1;
    grid-column: span 1;
  }

  ${BasicInformationSubGrid} {
    grid-row: role-start / bio-end;
    grid-column: 1 / -1;

    display: grid;
    grid:
      "role" min-content
      "bio" min-content
      / auto;
  }

  ${Role} {
    grid-area: role;
  }

  ${MobileMoreInformation}, ${TabletUpMoreInformation} {
    grid-area: more-info;
  }

  ${MobileBio}, ${TabletUpBio} {
    grid-area: bio;
  }

  ${Expertise} {
    grid-area: expertise;
  }

  ${Language} {
    grid-area: language;
  }

  ${MoreButton} {
    grid-area: more;

    place-self: start;
  }

  ${StyledSCMPLabLogo} {
    grid-area: scmp-lab-logo;
  }
`;
